import { ref } from "vue";
import { collection, doc, getDoc, getDocFromServer, getDocs, increment, limit, orderBy, query, setDoc, updateDoc, where, getCountFromServer, or, addDoc, serverTimestamp, onSnapshot, runTransaction, writeBatch } from "firebase/firestore";
import { db } from '@/firebase/firebaseInit';
import { generateNameTokens } from '@/utils';

export default {
    async checkIfBeneficiaryExists(context, payload) {
        // Create a reference to the document
        const docRef = doc(db, 'beneficiaries', String(payload));
      
        try {
            // Retrieve the document
            const docSnap = await getDoc(docRef);
        
            // Check if the document exists
            if (docSnap.exists()) {
                return true;
            } else {
                throw new Error("Beneficiary with id '" + payload + "' doesn't exist.");
            }
        } catch (error) {
            console.error("Error checking document existence:", error);
            throw error;
        }
        
    },

    async loadBeneficiaries(context, searchData) {
        const beneficiaries = [];
        if (searchData.searchBy == 'id') {
            const docRef = doc(db, "beneficiaries", String(searchData.searchTerm));
            const docSnap = searchData.forceServer
                ? await getDocFromServer(docRef)
                : await getDoc(docRef);

            if (docSnap.exists()) {
                const beneficiary = docSnap.data();
                beneficiaries.push(beneficiary);
            }
        }
        else if (searchData.searchBy === 'full_name') {
            const trigram = searchData.searchTerm.toLowerCase().trim().substring(0, 3);
            const q = query(collection(db, "beneficiaries"), where('name_tokens', 'array-contains', trigram));
            const querySnapshot = await getDocs(q);
            const lowerTerm = searchData.searchTerm.toLowerCase().trim();
            querySnapshot.forEach((doc) => {
                const beneficiary = doc.data();
                if (beneficiary.full_name && beneficiary.full_name.toLowerCase().includes(lowerTerm)) {
                    beneficiaries.push(beneficiary);
                }
            });
            beneficiaries.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
        }
        else {
            const beneficiaires = query(collection(db, "beneficiaries"),
                where(searchData.searchBy, ">=", searchData.searchTerm),
                where(searchData.searchBy, "<=", searchData.searchTerm + '\uf8ff'));

            const querySnapshot = await getDocs(beneficiaires);
            querySnapshot.forEach((doc) => {
                const beneficiary = doc.data();
                beneficiaries.push(beneficiary);
            });
        }
        context.commit('setBeneficiaries', beneficiaries);
        return beneficiaries;
    },

    async updateBeneficiary(context, { beneficiary, expectedUpdatedAt }) {
        const beneficiaryRef = doc(db, "beneficiaries", String(beneficiary.id));
        await runTransaction(db, async (transaction) => {
            const currentDoc = await transaction.get(beneficiaryRef);
            if (
                expectedUpdatedAt &&
                currentDoc.data().updated_at &&
                currentDoc.data().updated_at.toMillis() !== expectedUpdatedAt.toMillis()
            ) {
                throw new Error('CONCURRENCY_CONFLICT');
            }
            transaction.update(beneficiaryRef, {
                active: beneficiary.active,
                approved_by: beneficiary.approved_by,
                block: beneficiary.block,
                contact_number: beneficiary.contact_number,
                date_of_approval: beneficiary.date_of_approval,
                date_of_birth: beneficiary.date_of_birth,
                delivery_sort: beneficiary.delivery_sort,
                driving_delivery: beneficiary.driving_delivery,
                dry_bag: beneficiary.dry_bag,
                full_name: beneficiary.full_name,
                gender: beneficiary.gender,
                nric: beneficiary.nric,
                postal_code: beneficiary.postal_code,
                quantity: beneficiary.quantity,
                race: beneficiary.race,
                remarks: beneficiary.remarks,
                self_collection: beneficiary.self_collection,
                street: beneficiary.street,
                unit: beneficiary.unit,
                bag_status: beneficiary.bag_status,
                dietary_requirement: beneficiary.dietary_requirement,
                interview_personal: beneficiary.interview_personal,
                interview_financial: beneficiary.interview_financial,
                interview_observations: beneficiary.interview_observations,
                last_activation_date: beneficiary.last_activation_date,
                location: beneficiary.location,
                delivery_notes: beneficiary.delivery_notes || '',
                created: true,
                updated_at: serverTimestamp(),
                name_tokens: generateNameTokens(beneficiary.full_name)
            });
        });
    },

    async updateBagStatus(context, payload) {
        const beneficiaryRef = doc(db, "beneficiaries", String(payload.beneficiaryID));
        if (payload.deliveryStatus == 'DELIVERED') {

            if (payload.bagStatus == 'HAS_NO_BAG') {
                await updateDoc(beneficiaryRef, {
                    bag_status: 'HAS_A_BAG',
                    bag_not_returned_counter: 0
                });
            }

            else if (payload.bagStatus == 'HAS_A_BAG') {
                if (payload.bagCollected) {
                    // Still udpating to cater for Update scenarios
                    console.log("Updating bag status to HAS_A_BAG");
                    await updateDoc(beneficiaryRef, {
                        bag_status: 'HAS_A_BAG',
                        bag_not_returned_counter: 0
                    });
                } else {
                    // Beneficiary has a bag, but previous bag was NOT collected
                    console.log("Updating bag status to NOT_RETURNED");
                    await updateDoc(beneficiaryRef, {
                        bag_status: 'NOT_RETURNED',
                        bag_not_returned_counter: 1
                    });
                }
            }

            else if (payload.bagStatus == 'NOT_RETURNED') {
                // Beneficiary didn't return the bag in the past
                if (payload.bagCollected) {
                    // BUT today returned
                    console.log("Updating bag status to HAS_A_BAG");
                    await updateDoc(beneficiaryRef, {
                        bag_status: 'HAS_A_BAG',
                        bag_not_returned_counter: 0
                    });
                } else {
                    // NOT returned today either
                    console.log("Updating bag status to NOT_RETURNED and increment 'bag_not_returned_counter'");
                    await updateDoc(beneficiaryRef, {
                        bag_status: 'NOT_RETURNED',
                        bag_not_returned_counter: increment(1)
                    });
                }
            }
        }
    },

    async createBeneficiary() {
        const q = query(collection(db, "beneficiaries"), orderBy("id", "desc"), limit(1));
        const querySnapshot = await getDocs(q);
        let latestID;
        querySnapshot.forEach((doc) => {
            latestID = doc.id;
        });
        let newBeneficiaryID = Number(latestID) + 1;
        await setDoc(doc(db, "beneficiaries", String(newBeneficiaryID)), {
            id: newBeneficiaryID,
            active: false,
            created: false, // This attribute will be set to true when the beneficiary is updated the first time.
            updated_at: serverTimestamp(),
            name_tokens: []
        });
        console.log("New beneficiary created with ID:" + newBeneficiaryID);

        return newBeneficiaryID;
    },

    async advancedSearchBeneficiaries(context, payload) {
        const coll = collection(db, "beneficiaries");
        const advancedSearchResults = [];
        let q = query(coll);
        const whereClauses = [];

        whereClauses.push(where("active", "==", payload.beneficiaryActive))

        if (payload.searchByBlock) {
            if(isOnlyNumbers(payload.searchByBlock)) {
                whereClauses.push(or(where("block", "==", parseInt(payload.searchByBlock)), where("block", "==", payload.searchByBlock)));
            } else {
                whereClauses.push(where("block", "==", payload.searchByBlock));
            }
        }

        if (payload.searchByStreet) {
            whereClauses.push(where("street", ">=", payload.searchByStreet));
            whereClauses.push(where("street", "<=", payload.searchByStreet + '\uf8ff'));
        }

        if (payload.filterByHalal) {
            whereClauses.push(where("dietary_requirement", "==", "Halal"));
        }

        if (payload.filterNotReturnedBags) {
            whereClauses.push(where("bag_status", "==", "NOT_RETURNED"));
        }

        if (payload.filterByLocation && payload.filterByLocation !== 'BOTH') {
            whereClauses.push(where("location", "==", payload.filterByLocation));
        }

        whereClauses.forEach((clause) => {
            q = query(q, clause);
          });

        if (payload.searchAction == 'COUNT') {
            const snapshot = await getCountFromServer(q);
            context.commit('setBeneficiaryAdvancedSearchCount', snapshot.data().count);
        } else if (payload.searchAction == 'COUNT_BAGS') {
            const snapshot = await getDocs(q);
            const totalBags = snapshot.docs.reduce((sum, doc) => sum + (doc.data().quantity || 0), 0);
            context.commit('setBeneficiaryAdvancedSearchCount', totalBags);
        } else if (payload.searchAction == 'LIST') {
            const snapshot = await getDocs(q);
            context.commit('setBeneficiaryAdvancedSearchCount', snapshot.docs.length);
            if (snapshot.docs.length > 0) {
                snapshot.forEach((doc) => {
                    advancedSearchResults.push(doc.data());
                    context.commit('setAdvancedSearchResults', advancedSearchResults);
                });
            } else {
                context.commit('setAdvancedSearchResults', null);
            }
        }
    },
    
    async getAlreadyCreatedBeneficiaries(context) {
        const beneficiaries = [];
        const q = query(collection(db, "beneficiaries"),
                where("created", "==", false));

        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
            const beneficiary = doc.data();
            beneficiaries.push(beneficiary);
        });
        context.commit('setCreatedBeneficiaries', beneficiaries);
        return beneficiaries;
    },

    async createBeneficiaryHistory(context, payload) {
        const historyRef = collection(doc(db, "beneficiaries", payload.id), "history");

        await addDoc(historyRef, {
            type: payload.type,
            note: payload.note.trim(),
            author: payload.author,
            timestamp: serverTimestamp(),
            changes: payload.changes
        });
    },

    async migrateBeneficiaryNameTokens() {
        const querySnapshot = await getDocs(collection(db, 'beneficiaries'));
        const batch = writeBatch(db);
        querySnapshot.forEach((document) => {
            const data = document.data();
            batch.update(document.ref, { name_tokens: generateNameTokens(data.full_name) });
        });
        await batch.commit();
        return querySnapshot.size;
    },

    async listenToHistory(context, payload) {
        const historyRef = collection(doc(db, "beneficiaries", payload), "history");
        const q = query(historyRef, orderBy("timestamp", "desc"));
      
        const histories = ref([]);

        onSnapshot(q, (snapshot) => {
            
            histories.value = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        }, (error) => {
          console.error("Error fetching history:", error);
        });
        context.commit("setBeneficiaryHistory", histories);
      }
}

function isOnlyNumbers(str) {
    // Regular expression to match only digits
    const regex = /^\d+$/;
  
    // Test if the string matches the regular expression
    return regex.test(str.trim());
  }